import React, { useState, useMemo, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

// ── Scene imports ──────────────────────────────────────
import sceneBedtimeReading from "../assets/scenes/scene-bedtime-reading.webp";
import sceneProblemSolving from "../assets/scenes/scene-problem-solving.webp";
import sceneCampfire from "../assets/scenes/scene-campfire.webp";
import sceneStorytimeDay from "../assets/scenes/scene-storytime-day.webp";
import sceneStorytimeNight from "../assets/scenes/scene-storytime-night.webp";
import sceneGardening from "../assets/scenes/scene-gardening.webp";
import sceneClassroom from "../assets/scenes/scene-classroom.webp";
import sceneFishing from "../assets/scenes/scene-fishing.webp";
import sceneBaking from "../assets/scenes/scene-baking.webp";
import scenePicnic from "../assets/scenes/scene-picnic.webp";
import sceneStarlightReading from "../assets/scenes/scene-starlight-reading.webp";
import sceneLibraryReading from "../assets/scenes/scene-library-reading.webp";
import sceneKnightQuest from "../assets/scenes/scene-knight-quest.webp";
import sceneMrRuffusMath from "../assets/scenes/scene-mr-ruffus-math.webp";
import scenePrincessTea from "../assets/scenes/scene-princess-tea.webp";
import sceneSprocketBaking from "../assets/scenes/scene-sprocket-baking.webp";
import sceneKnightCampfire from "../assets/scenes/scene-knight-campfire.webp";
import sceneMuhpySleeping from "../assets/scenes/scene-muphy-sleeping.webp";
import sceneKnightPrincessFishing from "../assets/scenes/scene-knight-princess-fishing.webp";
import sceneSprocketLamp from "../assets/scenes/scene-sprocket-lamp.webp";
import scenePrincessPainting from "../assets/scenes/scene-princess-painting.webp";
import sceneMrRuffusFrisbee from "../assets/scenes/scene-mr-ruffus-frisbee.webp";
import sceneMuhpyBath from "../assets/scenes/scene-muphy-bath.webp";
import sceneMrsBedtime from "../assets/scenes/scene-mrs-bedtime.webp";
import sceneMuhpyReading from "../assets/scenes/scene-muphy-reading.webp";
import sceneMrsPrincessStudy from "../assets/scenes/scene-mrs-princess-study.webp";
import sceneBuilding from "../assets/scenes/scene-building.webp";
import sceneMrSprocketStudy from "../assets/scenes/scene-mr-sprocket-study.webp";

const ALL_SCENES = [
  sceneBedtimeReading, sceneProblemSolving, sceneCampfire,
  sceneStorytimeDay, sceneStorytimeNight, sceneGardening,
  sceneClassroom, sceneFishing, sceneBaking, scenePicnic,
  sceneStarlightReading, sceneLibraryReading, sceneKnightQuest,
  sceneMrRuffusMath, scenePrincessTea, sceneSprocketBaking,
  sceneKnightCampfire, sceneMuhpySleeping, sceneKnightPrincessFishing,
  sceneSprocketLamp, scenePrincessPainting, sceneMrRuffusFrisbee,
  sceneMuhpyBath, sceneMrsBedtime, sceneMuhpyReading,
  sceneMrsPrincessStudy, sceneBuilding, sceneMrSprocketStudy,
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 768);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Pick a random scene once when the page loads — useMemo
  // means it won't re-pick on every keystroke the user types
  const scene = useMemo(() => {
    const index = Math.floor(Math.random() * ALL_SCENES.length);
    return ALL_SCENES[index];
  }, []);

  async function handleSubmit() {
    try {
      setError("");
      setLoading(true);
      await login(email, password);
      navigate("/");
    } catch {
      setError("Failed to log in. Check your email and password.");
    }
    setLoading(false);
  }

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    fontFamily: "'Nunito', sans-serif",
  },
  imagePanel: {
    flex: isMobile ? "none" : 1,
    height: isMobile ? 260 : "auto",
    backgroundSize: "cover",
    backgroundPosition: "center top",
    backgroundRepeat: "no-repeat",
    display: "flex",
    alignItems: "flex-end",
    minHeight: isMobile ? "unset" : 300,
  },
  imagePanelOverlay: {
    width: "100%",
    padding: "24px 32px",
    background: "linear-gradient(to top, rgba(0,0,0,0.65) 0%, transparent 100%)",
  },
  tagline: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 28,
    fontWeight: 900,
    color: "#ffffff",
    margin: "0 0 4px 0",
    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
  },
  taglineSub: {
    fontFamily: "'Nunito', sans-serif",
    fontSize: 16,
    color: "#f0e6c8",
    margin: 0,
    textShadow: "0 1px 3px rgba(0,0,0,0.5)",
  },
  formPanel: {
    width: isMobile ? "100%" : 420,
    minWidth: isMobile ? "unset" : 320,
    backgroundColor: "#fdf6e3",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: isMobile ? "32px 20px" : "40px 24px",
    boxSizing: "border-box",
  },
  card: {
    width: "100%",
    maxWidth: 380,
  },
  title: {
    fontSize: 28,
    fontWeight: 900,
    color: "#3d2b00",
    margin: "0 0 6px 0",
    fontFamily: "'Nunito', sans-serif",
  },
  subtitle: {
    fontSize: 15,
    color: "#7a5c1e",
    margin: "0 0 28px 0",
    fontFamily: "'Nunito', sans-serif",
  },
  label: {
    display: "block",
    fontSize: 13,
    fontWeight: 700,
    color: "#3d2b00",
    marginBottom: 6,
    fontFamily: "'Nunito', sans-serif",
  },
  input: {
    width: "100%",
    padding: "12px 14px",
    marginBottom: 18,
    borderRadius: 10,
    border: "2px solid #d4a847",
    fontSize: 15,
    boxSizing: "border-box",
    backgroundColor: "#ffffff",
    color: "#3d2b00",
    fontFamily: "'Nunito', sans-serif",
    outline: "none",
  },
  button: {
    width: "100%",
    padding: 14,
    backgroundColor: "#2e7d32",
    color: "white",
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 800,
    cursor: "pointer",
    fontFamily: "'Nunito', sans-serif",
    marginBottom: 16,
  },
  buttonDisabled: {
    width: "100%",
    padding: 14,
    backgroundColor: "#a5c8a7",
    color: "white",
    border: "none",
    borderRadius: 10,
    fontSize: 16,
    fontWeight: 800,
    cursor: "not-allowed",
    fontFamily: "'Nunito', sans-serif",
    marginBottom: 16,
  },
  error: {
    color: "#c53030",
    marginBottom: 16,
    fontSize: 14,
    fontFamily: "'Nunito', sans-serif",
  },
  link: {
    textAlign: "center",
    fontSize: 14,
    color: "#7a5c1e",
    cursor: "pointer",
    fontFamily: "'Nunito', sans-serif",
  },
  linkAccent: {
    color: "#2e7d32",
    fontWeight: 700,
    textDecoration: "underline",
  },
};

  return (
    <div style={styles.container}>

      {/* Left panel — random scene image */}
      <div style={{ ...styles.imagePanel, backgroundImage: `url(${scene})` }}>
        <div style={styles.imagePanelOverlay}>
          <p style={styles.tagline}>📚 Clarke Kids!</p>
          <p style={styles.taglineSub}>Your reading adventure awaits.</p>
        </div>
      </div>

      {/* Right panel — login form */}
      <div style={styles.formPanel}>
        <div style={styles.card}>
          <h1 style={styles.title}>Welcome back!</h1>
          <p style={styles.subtitle}>Sign in to continue your quest.</p>

          {error && <p style={styles.error}>{error}</p>}

          <label style={styles.label}>Email</label>
          <input
            style={styles.input}
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />

          <label style={styles.label}>Password</label>
          <input
            style={styles.input}
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />

          <button
            style={loading ? styles.buttonDisabled : styles.button}
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? "Signing in..." : "Sign In ➜"}
          </button>

          <p style={styles.link} onClick={() => navigate("/register")}>
            Don't have an account? <span style={styles.linkAccent}>Register here</span>
          </p>
        </div>
      </div>
    </div>
  );
}